/**
 * Page Tour Hook
 *
 * The main hook used by each page component. Orchestrates:
 * 1. Feature flag check (is walkthrough enabled for this user?)
 * 2. First-visit detection (has user toured this page?)
 * 3. Welcome modal state management
 * 4. Tour lifecycle (start, complete, skip)
 * 5. Help button replay via WalkthroughContext
 *
 * CRM adaptation:
 * - Reads walkthrough settings directly from Firestore
 *   (path: referral-crm/data/config/walkthrough)
 * - Uses useAuth() instead of useOrgSettings()
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/services/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useWalkthrough } from './useWalkthrough';
import { useTourTracking } from './useTourTracking';
import { useWalkthroughContext } from './WalkthroughContext';
import type { TourConfig, WalkthroughSettings } from './types';

interface UsePageTourResult {
  /** Whether the welcome modal should be shown */
  showWelcomeModal: boolean;
  /** Start the tour (from welcome modal "Take the Tour" button) */
  startTour: () => void;
  /** Skip the tour (from welcome modal "Skip" button) */
  skipTour: () => void;
  /** Global opt-out (from "Don't show walkthroughs" checkbox) */
  globalOptOut: () => void;
  /** Replay the tour (from Help button) */
  replayTour: () => void;
  /** Whether walkthroughs are enabled for this user */
  walkthroughEnabled: boolean;
}

/**
 * Hook to read walkthrough settings from Firestore.
 * Path: referral-crm/data/config/walkthrough
 */
function useWalkthroughSettings(): WalkthroughSettings | null {
  const [settings, setSettings] = useState<WalkthroughSettings | null>(null);

  useEffect(() => {
    const docRef = doc(db, 'referral-crm/data/config', 'walkthrough');

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setSettings(snapshot.data() as WalkthroughSettings);
        } else {
          setSettings(null);
        }
      },
      (err) => {
        console.error('[useWalkthroughSettings] Error:', err);
        setSettings(null);
      }
    );

    return () => unsubscribe();
  }, []);

  return settings;
}

export function usePageTour(config: TourConfig): UsePageTourResult {
  const { user } = useAuth();
  const walkthroughSettings = useWalkthroughSettings();
  const { tracking, loading: trackingLoading, markToured, markSkipped, setGlobalOptOut } = useTourTracking();
  const { registerTour, unregisterTour, setWalkthroughEnabled } = useWalkthroughContext();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const hasShownRef = useRef(false);

  // Check feature flag
  const walkthroughEnabled = (() => {
    if (!walkthroughSettings?.enabled) return false;
    if (!user?.id) return false;

    // If specific user IDs are set, only those users see walkthroughs
    const enabledUsers = walkthroughSettings.enabledUserIds;
    if (enabledUsers && enabledUsers.length > 0) {
      return enabledUsers.includes(user.id);
    }

    // Master enabled + no specific users = everyone sees it
    return true;
  })();

  // Tour version from walkthrough settings
  const tourVersion = walkthroughSettings?.tourVersion ?? 1;

  // Walkthrough hook with completion/exit handlers
  const { startTour: startIntroTour } = useWalkthrough(walkthroughEnabled ? config : null, {
    onComplete: () => {
      markToured(config.pageId, tourVersion);
    },
    onExit: () => {
      // User exited early -- still mark as toured (they've seen part of it)
      markToured(config.pageId, tourVersion);
    },
  });

  // Register this tour with the context (for Header's Help button)
  useEffect(() => {
    if (walkthroughEnabled) {
      registerTour(config, startIntroTour);
      setWalkthroughEnabled(true);
    }
    return () => unregisterTour();
  }, [walkthroughEnabled, config, startIntroTour, registerTour, unregisterTour, setWalkthroughEnabled]);

  // Check if we should show the welcome modal for this page
  useEffect(() => {
    if (!walkthroughEnabled || trackingLoading || hasShownRef.current) return;

    // User opted out globally
    if (tracking?.globalOptOut) return;

    const pageState = tracking?.pages?.[config.pageId];

    // Already toured or skipped at current version
    if (pageState?.toured && pageState.tourVersion === tourVersion) return;
    if (pageState?.skipped) return;

    // Show welcome modal after a short delay (let page render first)
    const timer = setTimeout(() => {
      setShowWelcomeModal(true);
      hasShownRef.current = true;
    }, 800);

    return () => clearTimeout(timer);
  }, [walkthroughEnabled, trackingLoading, tracking, config.pageId, tourVersion]);

  const startTour = useCallback(() => {
    setShowWelcomeModal(false);
    // Small delay so modal closes before tour starts
    setTimeout(() => startIntroTour(), 100);
  }, [startIntroTour]);

  const skipTour = useCallback(() => {
    setShowWelcomeModal(false);
    markSkipped(config.pageId);
  }, [config.pageId, markSkipped]);

  const globalOptOutHandler = useCallback(() => {
    setShowWelcomeModal(false);
    setGlobalOptOut(true);
  }, [setGlobalOptOut]);

  const replayTour = useCallback(() => {
    startIntroTour();
  }, [startIntroTour]);

  return {
    showWelcomeModal,
    startTour,
    skipTour,
    globalOptOut: globalOptOutHandler,
    replayTour,
    walkthroughEnabled,
  };
}
